import { computed, reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import { Howl, Howler } from 'howler';
import { assignState } from '@/store/index.js';
import useAuthStore from '@/store/modules/auth.js';
import { useLocalStorageHook } from '@/hooks/usePersistentStorage.js';
import { musicStorageKey, soundStorageKey } from '@/constants/persistentStorage.js';
import { appFlowModeEnum, appFlowTypeEnum } from '@/constants/appSettings.js';
import {
  apiRetrieveGtmInfo,
  apiRetrieveAppProperties,
  apiRetrieveMultiOperatorProperties,
} from '@/services/appSettings.js';

const backgroundMusic = new Howl({
  src: ['/static/sounds/music.mp3'],
  loop: true,
  volume: 0.5,
  preload: true,
});

const useAppSettingsStore = defineStore('appSettings', () => {
  const authStore = useAuthStore();

  const [isMusicEnabled, setMusicState] = useLocalStorageHook(musicStorageKey, true);
  const [isSoundEnabled, setSoundState] = useLocalStorageHook(soundStorageKey, true);

  const appProperties = reactive({
    appFlowType: '',
    duelSkin: null,
    fallingItemsGameCss: '',
    gamePreModalTime: null,
    gameRoundTimeList: [],
    gameTheme: '',
    hideChangePassword: false,
    hideLeaderboard: false,
    hidePoints: false,
    hidePrizePage: false,
    hidePrizeSectionFromLeaderboard: false,
    hideUserAchievements: false,
    hideWinners: false,
    integrationName: '',
    inviteConfig: { inviteEnabled: false, invitePoints: null },
    isPurchaseBundleFromUnsubUserEnabled: false,
    isReoptinOneStep: false,
    isSingleOptinEnabled: false,
    isTwoStepFlow: false,
    maxLengthPinDigits: null,
    multiOperatorDisplayNames: null,
    multiOperatorDisplayedNamesInRegisterPage: null,
    multiOperatorEnabled: false,
    multiOperatorKeys: null,
    phoneShortcutEnabled: true,
    puzzleGameCss: '',
    showMsisdnInProfilePage: false,
    showVatBanner: false,
    trackingId: null,
    timerForPuzzleGame: null,
    timerForLanternGame: null,
    winnersShowMsisdn: false,
  });

  const multiOperatorProperties = reactive({
    displayPrice: false,
    enablePopUpForCrossPromotion: false,
    hideStore: false,
    isSubAndUnsubViaSMSEnabled: false,
    multiOperatorOrdinal: null,
    pinForBundles: false,
    purchaseViaSMS: false,
    reoptinAndOptoutConfig: '',
    showOptOutPhase: false,
    smsAutomaticResponse: false,
  });

  const gtmKey = ref('');

  const isPrizesPageDisabled = computed(() => appProperties.hidePrizePage);
  const isPointsHidden = computed(() => appProperties.hidePoints);
  const isWinnersDisabled = computed(() => appProperties.hideWinners);
  const isStoreDisabled = computed(() => multiOperatorProperties.hideStore);
  const isLeaderboardDisabled = computed(() => appProperties.hideLeaderboard || appProperties.hidePrizeSectionFromLeaderboard);
  const isResubscribeViaSmsSupported = computed(() =>
    multiOperatorProperties.isSubAndUnsubViaSMSEnabled &&
    ['REOPTIN_SMS', 'REOPTIN_OPTOUT_SMS'].includes(multiOperatorProperties.reoptinAndOptoutConfig),
  );
  const isUnsubscribeViaSmsSupported = computed(() =>
    multiOperatorProperties.isSubAndUnsubViaSMSEnabled &&
    ['OPTOUT_SMS', 'REOPTIN_OPTOUT_SMS'].includes(multiOperatorProperties.reoptinAndOptoutConfig),
  );
  const appFlowMode = computed(() => {
    if (appProperties.appFlowType === appFlowTypeEnum.DEFAULT) {
      return appProperties.isTwoStepFlow ? appFlowModeEnum.TWO_STEP_DEFAULT : appFlowModeEnum.GENERAL_DEFAULT;
    } else if (appProperties.appFlowType === appFlowTypeEnum.SIMPLE_WITH_WEB_REGISTRATION) {
      return appProperties.isTwoStepFlow ? appFlowModeEnum.TWO_STEP_SIMPLE_WITH_WEB_REGISTRATION : appFlowModeEnum.GENERAL_SIMPLE_WITH_WEB_REGISTRATION;
    } else if (appProperties.appFlowType === appFlowTypeEnum.SIMPLE_WITHOUT_WEB_REGISTRATION) {
      return appProperties.isTwoStepFlow ? appFlowModeEnum.TWO_STEP_SIMPLE_WITHOUT_WEB_REGISTRATION : appFlowModeEnum.GENERAL_SIMPLE_WITHOUT_WEB_REGISTRATION;
    } else {
      return '';
    }
  });

  const playMusic = () => {
    backgroundMusic.play();
    Howler.volume(0.8);
  };

  const pauseMusic = () => {
    backgroundMusic.pause();
  };

  const enableMusic = () => {
    setMusicState(true);
    playMusic();
  };

  const disableMusic = () => {
    setMusicState(false);
    pauseMusic();
  };

  const dispatchRetrieveAppProperties = async () => {
    try {
      const { parsedData } = await apiRetrieveAppProperties();

      assignState(appProperties, parsedData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const dispatchRetrieveMultiOperatorProperties = async ({ operator } = {}) => {
    try {
      const queryParams = {
        operatorKey: operator || authStore.user.operatorName || '',
      };

      const { parsedData } = await apiRetrieveMultiOperatorProperties({ queryParams });

      assignState(multiOperatorProperties, parsedData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const dispatchRetrieveGtmInfo = async () => {
    try {
      const { parsedData } = await apiRetrieveGtmInfo();

      assignState(gtmKey, parsedData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  return {
    isMusicEnabled,
    isSoundEnabled,
    appProperties,
    multiOperatorProperties,
    gtmKey,
    isPrizesPageDisabled,
    isPointsHidden,
    isWinnersDisabled,
    isStoreDisabled,
    isLeaderboardDisabled,
    isResubscribeViaSmsSupported,
    isUnsubscribeViaSmsSupported,
    appFlowMode,
    setSoundState,
    playMusic,
    pauseMusic,
    enableMusic,
    disableMusic,
    dispatchRetrieveAppProperties,
    dispatchRetrieveMultiOperatorProperties,
    dispatchRetrieveGtmInfo,
  };
});

export default useAppSettingsStore;
